import { NgClass } from '@angular/common'
import { ChangeDetectionStrategy, Component, computed, input, output, TrackByFunction } from '@angular/core'
import { InvitationChannelHistoryState, InvitationCollectionItem } from '@app/v2/event/types/invitation-tracker.type'
import { InvitationChannelSendRequest } from '@app/v2/event/utils/invitation-channel-ui.util'
import {
  canRemoveInvitationCollectionItem,
  formatInvitationCollectionActionTakenLabel,
  invitationCollectionActionTakenBadgeClass
} from '@app/v2/event/utils/invitation-action-taken.util'
import {
  getInvitationChannelLatestStatus,
  getInvitationCollectionChannelStatuses,
  InvitationChannel,
  INVITATION_CHANNEL_COMING_SOON_TOOLTIP,
  invitationChannelDotClass,
  invitationChannelIconFill,
  InvitationChannelStatusIndicator,
  isInvitationChannelAvailable
} from '@app/v2/event/utils/invitation-channel-status.util'
import { formatInvitationCollectionSourceLabel } from '@app/v2/event/utils/invitation-source.util'
import { GenericTableComponent } from '@app/v2/shared/components/generic-table/generic-table.component'
import { GENERIC_TABLE_MAX_VIEWPORT_HEIGHT_PX, GENERIC_TABLE_ROW_HEIGHT_PX } from '@app/v2/shared/components/generic-table/generic-table.constants'
import { TruncationTooltipComponent } from '@app/v2/shared/components/truncation-tooltip/truncation-tooltip.component'
import { GenericTableCellDefDirective } from '@app/v2/shared/directives/generic-table-cell-def.directive'
import { GenericTableColumnDirective } from '@app/v2/shared/directives/generic-table-column.directive'
import { GenericTableSelectAllCollectionChange, GenericTableSelectionChange, GenericTableSort } from '@app/v2/shared/types/generic-table.types'
import { computeGenericTableViewportHeight } from '@app/v2/shared/utils/generic-table-viewport.util'
import { NgxTippyModule } from 'ngx-tippy-wrapper'
import { InvitationChannelPopoverComponent } from '../invitation-channel-popover/invitation-channel-popover.component'

@Component({
  selector: 'app-desktop-invitation-collection-table',
  standalone: true,
  imports: [
    GenericTableComponent,
    GenericTableColumnDirective,
    GenericTableCellDefDirective,
    NgClass,
    NgxTippyModule,
    TruncationTooltipComponent,
    InvitationChannelPopoverComponent
  ],
  templateUrl: './desktop-invitation-collection-table.component.html',
  styleUrl: './desktop-invitation-collection-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DesktopInvitationCollectionTableComponent {
  public readonly items = input.required<InvitationCollectionItem[]>()
  public readonly selectedKeys = input.required<string[]>()
  public readonly isAllCollectionSelected = input(false)
  public readonly hasCollectionSelection = input(false)
  public readonly sort = input.required<GenericTableSort>()
  public readonly hasMore = input.required<boolean>()
  public readonly isLoadingMore = input.required<boolean>()
  public readonly isLoading = input(false)
  public readonly perPage = input.required<number>()
  public readonly trackBy = input.required<TrackByFunction<InvitationCollectionItem>>()
  public readonly rowKey = input.required<(item: InvitationCollectionItem) => string>()
  public readonly virtualScrollMinBufferPx = input(300)
  public readonly virtualScrollMaxBufferPx = input(600)
  public readonly isSendingInvitation = input(false)
  public readonly channelHistory = input<InvitationChannelHistoryState | null>(null)
  public readonly isEventFinished = input(false)
  public readonly responsiveColumns = input(true)
  public readonly highlightedRowKey = input<string | null>(null)

  public readonly selectionChange = output<GenericTableSelectionChange<InvitationCollectionItem>>()
  public readonly selectAllCollectionChange = output<GenericTableSelectAllCollectionChange>()
  public readonly sortChange = output<GenericTableSort>()
  public readonly loadMore = output<void>()
  public readonly removeItem = output<InvitationCollectionItem>()
  public readonly sendInvitation = output<InvitationChannelSendRequest>()
  public readonly channelPanelOpened = output<InvitationChannelSendRequest>()
  public readonly loadMoreChannelHistory = output<void>()
  public readonly sourceClick = output<InvitationCollectionItem>()

  public readonly tableRowHeightPx = GENERIC_TABLE_ROW_HEIGHT_PX
  //TODO: Remove this once SMS is supported
  public readonly comingSoonTooltip = INVITATION_CHANNEL_COMING_SOON_TOOLTIP
  public readonly formatActionTakenLabel = formatInvitationCollectionActionTakenLabel
  public readonly actionTakenBadgeClass = invitationCollectionActionTakenBadgeClass
  public readonly formatSourceLabel = formatInvitationCollectionSourceLabel
  public readonly channelDotClass = invitationChannelDotClass
  public readonly channelIconFill = invitationChannelIconFill
  public readonly isChannelAvailable = isInvitationChannelAvailable
  public readonly canRemoveItem = canRemoveInvitationCollectionItem

  public readonly channelStatusesByItemId = computed(() => {
    const statusesById = new Map<number, InvitationChannelStatusIndicator[]>()
    for (const item of this.items()) {
      statusesById.set(item.id, getInvitationCollectionChannelStatuses(item))
    }
    return statusesById
  })

  public readonly viewportHeightPx = computed(() =>
    computeGenericTableViewportHeight(this.items().length, this.perPage(), GENERIC_TABLE_ROW_HEIGHT_PX, GENERIC_TABLE_MAX_VIEWPORT_HEIGHT_PX, this.hasMore())
  )

  /**
   * Channel status indicators for a row (from the precomputed map).
   * @param item {InvitationCollectionItem} - Collection row.
   */
  public channelStatusesFor(item: InvitationCollectionItem): InvitationChannelStatusIndicator[] {
    return this.channelStatusesByItemId().get(item.id) ?? []
  }

  /**
   * Raw latest invitation status for a channel (used for blocked-send).
   * @param item {InvitationCollectionItem} - Collection row.
   * @param channel {InvitationChannel} - Channel to read.
   */
  public channelLatestStatusFor(item: InvitationCollectionItem, channel: InvitationChannel): string | null {
    return getInvitationChannelLatestStatus(item, channel)
  }

  /**
   * Forward remove action to the parent.
   * @param item {InvitationCollectionItem} - The collection item to remove.
   */
  public onRemoveItem(item: InvitationCollectionItem): void {
    this.removeItem.emit(item)
  }

  /**
   * Forward source click to the parent.
   * @param item {InvitationCollectionItem} - The collection item whose source was clicked.
   */
  public onSourceClick(item: InvitationCollectionItem): void {
    this.sourceClick.emit(item)
  }
}
